import { DateTime } from 'luxon';
import {
  Food, FoodHeader, FoodOrigin, getFoodOrigin, hasFoodBeenLoggedBefore, isLogMeal,
} from '@cf-shared/data-model/food';

/**
 * The filters on the Add Foods page: what they are, which foods they keep, how they are drawn as
 * chips, and how they are remembered on this device.
 *
 * This file is plain logic with no Angular, so a unit test can load it. It must never import
 * `foods.ts` or an Angular service: the page pulls in the whole Angular dependency graph, which
 * the unit test runner cannot load. That is also why nothing here logs — `restoreRemembered`
 * reports a repaired value by returning it, and the page does the telemetry call.
 */

/** the one type a search is narrowed to; only one can be chosen at a time */
export enum FoodTypeFilter {
  All = 'all',
  Foods = 'foods',
  Recipes = 'recipes',
  Meals = 'meals',
}

/** every filter the page can apply at once; the booleans combine with each other and with the type */
export interface FoodsFilterState {
  type: FoodTypeFilter;
  /** foods the user created, including ones they later shared (#5323) */
  myFoods: boolean;
  /** foods the user liked */
  favorites: boolean;
  /** foods the user has logged before */
  loggedBefore: boolean;
  /** hide other members' foods and Open Food Facts (#3444) */
  hideUnverified: boolean;
}

/** nothing filtered: what a new user sees, and what the page falls back to */
export const DEFAULT_FOODS_FILTER_STATE: FoodsFilterState = {
  type: FoodTypeFilter.All,
  myFoods: false,
  favorites: false,
  loggedBefore: false,
  hideUnverified: false,
};

/** the boolean filters, in the order the Filters menu lists them */
const SHOW_ONLY_FILTERS = ['myFoods', 'favorites', 'loggedBefore', 'hideUnverified'] as const;
/** the name of one filter that is simply on or off, as opposed to the type */
export type FoodFilterName = typeof SHOW_ONLY_FILTERS[number];

/** the filters that keep a chip on the line even while they are off */
const FIXED_CHIPS: FoodFilterName[] = ['myFoods', 'favorites'];

/**
 * What the `foods_filter_toggle` event calls each filter. Hardcoded English, never shown on
 * screen: the labels on the chips are translated, so sending one of those would file a Spanish
 * user's tap under the Spanish word and make the event unsegmentable.
 */
export const FILTER_TELEMETRY_NAMES: { [name in FoodFilterName]: string } = {
  myFoods: 'my_foods',
  favorites: 'favorites',
  loggedBefore: 'logged_before',
  hideUnverified: 'hide_unverified',
};

/** true when the user liked this food; only a food read from the user's own database knows that */
function isLikedCachedFood(foodHeader: FoodHeader): boolean {
  return (foodHeader.cachedFood as Food)?.local?.liked > 0;
}

/**
 * True when the user has eaten this before.
 *
 * A logged meal counts, even though the shared `hasFoodBeenLoggedBefore` deliberately says no to
 * one: that helper answers a different question, "should this card carry the Logged Before mark",
 * and a meal is surfaced as a recent meal instead. Here the question is the plain one the filter's
 * name asks, and a meal in this list is by definition something the user ate.
 */
function hasBeenEatenBefore(foodHeader: FoodHeader): boolean {
  return isLogMeal(foodHeader.cachedFood) || hasFoodBeenLoggedBefore(foodHeader.cachedFood);
}

/**
 * Keeps only the headers that pass every filter. The filters combine with AND.
 *
 * Legacy saved meals (`foodHeader.meal`) are treated as recipes (`foodHeader.recipe = true`).
 * The "Meals" type shows only Log_Meal entries (recent or logged meals), not legacy saved meals.
 *
 * A hit that came from the server carries no `cachedFood`, so it cannot be liked or logged before
 * and is not the user's own. Those three filters therefore drop it, which is correct: a food the
 * user liked, logged or created is always on their own device, and that copy replaces the server
 * hit before the list is shown. Hide Unverified works on any hit, needing only the id and status.
 */
export function applyFoodFilters<T extends FoodHeader>(headers: T[], state: FoodsFilterState): T[] {
  return headers.filter(foodHeader => {
    // filter by food type
    if (
      (state.type === FoodTypeFilter.Foods && (foodHeader.recipe || isLogMeal(foodHeader.cachedFood))) ||
      (state.type === FoodTypeFilter.Recipes && !foodHeader.recipe) ||
      (state.type === FoodTypeFilter.Meals && !isLogMeal(foodHeader.cachedFood))
    ) {
      return false;
    }
    // if only the user's own foods are wanted. Asked of getFoodOrigin rather than tested here, so
    // this filter and the person icon on the card can never disagree about whose food it is
    if (state.myFoods && getFoodOrigin(foodHeader) !== FoodOrigin.OwnFood) {
      return false;
    }
    if (state.favorites && !isLikedCachedFood(foodHeader)) {
      return false;
    }
    if (state.loggedBefore && !hasBeenEatenBefore(foodHeader)) {
      return false;
    }
    // if unchecked sources are hidden: another member's food, or an Open Food Facts product
    if (state.hideUnverified) {
      const origin = getFoodOrigin(foodHeader);
      if (origin === FoodOrigin.MemberFood || origin === FoodOrigin.OpenFoodFacts) {
        return false;
      }
    }
    return true;
  });
}

/** how many filters are not at their default; the Filters icon shows this, Type included */
export function activeFilterCount(state: FoodsFilterState): number {
  let count = state.type === FoodTypeFilter.All ? 0 : 1;
  for (const filter of SHOW_ONLY_FILTERS) {
    if (state[filter]) {
      count++;
    }
  }
  return count;
}

/** one chip on the line under the search box */
export interface FoodFilterChip {
  /** which filter it turns on and off; the type chip is drawn separately, ahead of these */
  id: FoodFilterName;
  on: boolean;
}

/**
 * The chips to draw after the type chip, in order: the filters that are ON first, in the order
 * the Filters menu lists them, then My Foods and Favorites when they are off. So a person can see
 * why the list looks as it does, and the two chips the design fixed on are always reachable.
 */
export function buildChipLine(state: FoodsFilterState): FoodFilterChip[] {
  const chips: FoodFilterChip[] = [];
  for (const filter of SHOW_ONLY_FILTERS) {
    if (state[filter]) {
      chips.push({ id: filter, on: true });
    }
  }
  for (const filter of FIXED_CHIPS) {
    if (!state[filter]) {
      chips.push({ id: filter, on: false });
    }
  }
  return chips;
}

/**
 * What the page actually filters by: the user's remembered filters, then whatever a route set for
 * this visit, then whatever a barcode search set until the next ordinary search.
 *
 * Layering rather than overwriting is what lets the scan layer be dropped: after a barcode search
 * on a visit opened from "View All Recent Meals", clearing the scan returns the type to the
 * route's Meals rather than to the remembered type.
 */
export function combineFilterState(
  remembered: FoodsFilterState,
  route: Partial<FoodsFilterState>,
  scan: Partial<FoodsFilterState>,
): FoodsFilterState {
  return { ...remembered, ...route, ...scan };
}

/** the shape written to this device's storage; `v` lets a later shape be recognised and dropped */
interface RememberedFilters {
  v: 1;
  uid: string;
  savedAt: string;
  state: FoodsFilterState;
}

const REMEMBERED_VERSION = 1;
/** filters left on from an earlier session switch off after this long away from the page */
const REMEMBER_FOR_MINUTES = 60;

/** what the page writes to storage after every filter change, and whenever it is entered or left */
export function serializeRemembered(state: FoodsFilterState, uid: string, now: DateTime): string {
  const remembered: RememberedFilters = {
    v: REMEMBERED_VERSION,
    uid,
    savedAt: now.toUTC().toISO(),
    state,
  };
  return JSON.stringify(remembered);
}

/** what `restoreRemembered` answers: the filters to use, and whether a damaged value was repaired */
export interface RestoredFilters {
  state: FoodsFilterState;
  /** true only when the stored value was damaged, so the page reports it; expiry is not damage */
  repaired: boolean;
  /** what was wrong, for the report */
  reason?: string;
}

/** true when every field of the parsed state has the type this file expects */
function isWellFormedState(state: unknown): state is FoodsFilterState {
  if (!state || typeof state !== 'object') {
    return false;
  }
  const candidate = state as Partial<FoodsFilterState>;
  if (!Object.values(FoodTypeFilter).includes(candidate.type)) {
    return false;
  }
  for (const filter of SHOW_ONLY_FILTERS) {
    if (typeof candidate[filter] !== 'boolean') {
      return false;
    }
  }
  return true;
}

/**
 * Reads back what `serializeRemembered` wrote, and falls back to the default filters whenever it
 * cannot be trusted: nothing saved, a shape from a version this code does not know, a field of the
 * wrong type, another account's filters, or a save older than an hour.
 *
 * It never throws — a document written by an older version of the app, or damaged outright, is
 * data to repair rather than a failure to hide. Only a damaged value counts as repaired; an empty
 * store, another account and an expired save are all ordinary and are reported as nothing at all.
 */
export function restoreRemembered(raw: string | undefined | null, uid: string, now: DateTime): RestoredFilters {
  const fallback = { state: { ...DEFAULT_FOODS_FILTER_STATE }, repaired: false };
  if (!raw) {
    return fallback;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // expected: anything at all can be in storage, including a half-written or hand-edited value
    return { ...fallback, repaired: true, reason: 'unreadable' };
  }

  const remembered = parsed as Partial<RememberedFilters>;
  if (!remembered || typeof remembered !== 'object' || remembered.v !== REMEMBERED_VERSION) {
    return { ...fallback, repaired: true, reason: 'unknown version' };
  }
  if (!isWellFormedState(remembered.state)) {
    return { ...fallback, repaired: true, reason: 'wrong field type' };
  }
  // another account signed in on this device since; their filters are none of this user's business
  if (remembered.uid !== uid) {
    return fallback;
  }

  const savedAt = DateTime.fromISO(remembered.savedAt ?? '');
  if (!savedAt.isValid) {
    return { ...fallback, repaired: true, reason: 'unreadable savedAt' };
  }
  // if the user has been away from the page for over an hour, start from the default filters again
  if (now.diff(savedAt).as('minutes') > REMEMBER_FOR_MINUTES) {
    return fallback;
  }

  return { state: { ...remembered.state }, repaired: false };
}
